import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { catchError, EMPTY, map, Observable, of, switchMap, tap } from 'rxjs';
import { DateTime } from 'ts-luxon';

import { OktaInterfaceService } from '@shure/cloud/shared/okta/data-access';
import { ElevatedRequestDto, ElevatedRequestStatus } from '@shure/cloud/shared/organizations/data-access';
import {
	AccountInformationDto,
	OrganizationsStoreService
} from '@shure/cloud/shared/services/organizations-store-service';
import { RealTimeInAppBrowserNotificationService } from '@shure/cloud/shared/services/websocket';
import { getConvertedDateTime, updateDateFormat } from '@shure/cloud/shared/ui/components';
import { ApiUsersService } from '@shure/cloud/shared/users/data-access';
import {
	PrismBannerComponent,
	PrismButtonComponent,
	PrismDialogService,
	PrismIconComponent,
	PrismSnackbarService
} from '@shure/prism-angular-components';

import { ConfirmDialog, ConfirmDialogResult } from './lib/models/elevated-access.model';
import { ElevatedAccessApiService } from './lib/services/elevated-access-api.service';
import { ElevatedAccessService } from './lib/services/elevated-access.service';
import { ConfirmDialogComponent } from './lib/ui/confirm-dialog/confirm-dialog.component';
import { GrantAccessDialogComponent } from './lib/ui/grant-access/grant-access-dialog.component';
@Component({
	selector: 'sh-elevated-access-main',
	imports: [CommonModule, TranslocoModule, PrismBannerComponent, PrismButtonComponent, PrismIconComponent],
	templateUrl: './elevated-access.component.html',
	styleUrl: './elevated-access.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ElevatedAccessMainComponent implements OnInit {
	public showAccessRequestBanner = signal(false);
	public elevatedRequestDetails = this.elevatedAccessService.elevatedRequest;
	public elevatedRequestStatus = ElevatedRequestStatus;
	public timeZone!: string | undefined;
	public displayDateFormat!: string;
	public formattedElevatedDetails = computed(() => {
		const elevatedRequest = this.elevatedRequestDetails();
		const accountInfo = this.organizationStore.accountInformation();
		if (elevatedRequest?.requestStatus.toLowerCase() === ElevatedRequestStatus.Granted) {
			if (!elevatedRequest?.expiresAt) {
				return null;
			}

			const timeZone = accountInfo.preferences?.timeZone;
			const displayDateFormat = accountInfo.preferences?.dateFormat
				? updateDateFormat(accountInfo.preferences.dateFormat.toLowerCase())
				: updateDateFormat('mm/dd/yyyy');

			const expiryDate = getConvertedDateTime(timeZone, displayDateFormat, elevatedRequest.expiresAt);
			const expiryDateTime = DateTime.fromISO(elevatedRequest.expiresAt).setZone(timeZone ?? 'UTC');
			const currentDateTime = DateTime.now().setZone(timeZone ?? 'UTC');
			const isExpired = currentDateTime > expiryDateTime;
			return {
				isExpired,
				expiresAt: expiryDate
			};
		}
		return null;
	});
	public elevatedRequestNotification: ElevatedRequestDto | null = null;
	constructor(
		private readonly dialogService: PrismDialogService,
		private apiUsersService: ApiUsersService,
		private readonly destroyRef: DestroyRef,
		private organizationStore: OrganizationsStoreService,
		private readonly translocoService: TranslocoService,
		private elevatedAccessApiService: ElevatedAccessApiService,
		private elevatedAccessService: ElevatedAccessService,
		private websocketService: RealTimeInAppBrowserNotificationService,
		private snackBarService: PrismSnackbarService,
		private readonly oktaIntfService: OktaInterfaceService
	) {
		effect(() => {
			const isElevatedRequestEnabled = this.elevatedAccessService.isElevatedRequestEnabled();
			if (!isElevatedRequestEnabled) {
				return;
			}
			// Update timezone and date format from account information
			const accountInfo = this.organizationStore.accountInformation();

			// Handle elevated access status notifications from WebSocket
			const elevatedRequestNotification = this.websocketService.notificationData();
			if (
				elevatedRequestNotification &&
				typeof elevatedRequestNotification === 'object' &&
				'elevatedRequest' in elevatedRequestNotification &&
				elevatedRequestNotification.elevatedRequest
			) {
				const elevatedRequest = elevatedRequestNotification.elevatedRequest;
				// Only process if notification has actually changed
				// if we have already handled the same notification (same requestId, status, expiry, and orgId),
				// we can skip processing to avoid unnecessary UI updates
				if (
					elevatedRequest.elevatedRequestId === this.elevatedRequestNotification?.elevatedRequestId &&
					elevatedRequest?.requestStatus.toLowerCase() ===
						this.elevatedRequestNotification?.requestStatus.toLowerCase() &&
					elevatedRequest?.expiresAt === this.elevatedRequestNotification?.expiresAt &&
					elevatedRequest?.organizationId === this.elevatedRequestNotification?.organizationId
				) {
					return; // Skip if no meaningful change
				}
				this.elevatedRequestNotification = <ElevatedRequestDto>elevatedRequestNotification.elevatedRequest;
				this.handleElevatedAccessWebSocketMessage(
					<ElevatedRequestDto>elevatedRequestNotification.elevatedRequest,
					accountInfo
				);
			}
		});
	}

	public ngOnInit(): void {
		const isElevatedRequestEnabled = this.elevatedAccessService.isElevatedRequestEnabled();
		if (isElevatedRequestEnabled) {
			this.getElevatedRequestDetails();
		}
	}

	/**
	 * Processes WebSocket notifications for elevated access request status changes.
	 * Handles three main scenarios:
	 * 1. Non-elevated users receiving notifications for their current organization - updates the request and banner.
	 * 2. Termination/revocation/expiration status - displays a confirmation dialog before switching organization or logging out.
	 * 3. Granted status - opens a new tab for different organizations or updates the current organization.
	 *
	 * @param elevatedRequestNotification - The elevated request notification containing status and organization details.
	 * @param accountInfo - The current user's account information including their organization context.
	 */
	public handleElevatedAccessWebSocketMessage(
		elevatedRequestNotification: ElevatedRequestDto,
		accountInfo: AccountInformationDto | null
	): void {
		const elevatedRequest = this.elevatedAccessService.getElevatedRequest();
		// Check if the elevatedRequest is already handled
		if (
			elevatedRequest?.elevatedRequestId === elevatedRequestNotification.elevatedRequestId &&
			elevatedRequest?.requestStatus.toLowerCase() === elevatedRequestNotification.requestStatus.toLowerCase() &&
			elevatedRequest?.expiresAt === elevatedRequestNotification.expiresAt &&
			elevatedRequest?.organizationId === elevatedRequestNotification.organizationId
		) {
			return; // Skip if it is already handled
		}
		// If non-elevated user receives a notification for their current organization,
		// update the request and refresh the banner
		if (!elevatedRequest?.isElevatedUser && !elevatedRequestNotification.isElevatedUser) {
			if (accountInfo?.organizationDetails?.organizationId === elevatedRequestNotification.organizationId) {
				this.elevatedAccessService.setElevatedRequest({ ...elevatedRequest, ...elevatedRequestNotification });
				this.updateAccessRequestBanner();
			}
			return;
		}

		// Handle termination/revocation/expiration status - show dialog and switch/logout
		if (
			accountInfo?.organizationDetails?.organizationId === elevatedRequestNotification.organizationId &&
			(elevatedRequestNotification.requestStatus.toLowerCase() === ElevatedRequestStatus.Terminated ||
				elevatedRequestNotification.requestStatus.toLowerCase() === ElevatedRequestStatus.Revoked ||
				elevatedRequestNotification.requestStatus.toLowerCase() === ElevatedRequestStatus.Expired)
		) {
			this.handleTerminatedStatus(elevatedRequestNotification);
		}
		// Handle granted status - open new tab for different org or update current org
		else if (
			elevatedRequestNotification.requestStatus.toLowerCase() === ElevatedRequestStatus.Granted.toLowerCase()
		) {
			if (accountInfo?.organizationDetails?.organizationId !== elevatedRequestNotification.organizationId) {
				const organizationId = elevatedRequestNotification.organizationId;
				window.open(window.location.origin + '/home?orgId=' + organizationId, organizationId);
			} else {
				this.elevatedAccessService.setElevatedRequest({ ...elevatedRequest, ...elevatedRequestNotification });
				this.updateAccessRequestBanner();
			}
		}
	}

	/**
	 * Handles the terminated status of an elevated access request.
	 * Opens a confirmation dialog to inform the user that their elevated access
	 * has been terminated. The dialog is displayed before
	 * triggering organization change or logout.
	 *
	 * @param elevatedRequestNotification - The elevated request notification containing
	 *                                      the organization ID and termination details.
	 */
	public handleTerminatedStatus(elevatedRequestNotification: ElevatedRequestDto | null): void {
		this.openConfirmDialog()
			.pipe(
				switchMap(() => {
					this.changeOrganization(elevatedRequestNotification?.defaultOrganizationId || '');
					return EMPTY;
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
	}

	/**
	 * Changes the current organization by updating the Okta session tenant.
	 * If the organization ID is valid, sets the session tenant and reloads the page.
	 * If the organization ID is invalid or empty, logs the user out.
	 * Clears permission-related cache after successful tenant switch.
	 *
	 * @param organizationId - The ID of the organization to switch to.
	 */
	public changeOrganization(organizationId: string): void {
		if (!organizationId) {
			this.logout();
			return;
		}
		this.oktaIntfService
			.setOktaSessionTenant$(organizationId, true)
			.pipe(
				tap(() => {
					window.location.reload();
				}),
				catchError(() => {
					return of(false);
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
	}

	/**
	 * Logs out the current user from the application.
	 * Clears the Okta session, removes locale and banner preferences from local storage,
	 * and redirects the user to the Okta logout page.
	 */
	public async logout(): Promise<void> {
		await this.oktaIntfService.signOut();
		localStorage.removeItem('locale');
		localStorage.removeItem('hideInfoBanner');
	}

	/**
	 * Opens a confirmation dialog to inform the user about access termination or revocation.
	 * The dialog displays a localized message indicating that access is no longer available.
	 * Returns an Observable that emits the dialog result when the user closes the dialog.
	 *
	 * @returns An Observable that emits the dialog result, or undefined if the dialog was dismissed.
	 */
	public openConfirmDialog(): Observable<ConfirmDialogResult | undefined> {
		const dialogRef = this.dialogService.openDialog<ConfirmDialogComponent, ConfirmDialog, unknown>(
			ConfirmDialogComponent,
			{
				data: {
					title: this.translocoService.translate('cloud.shared.elevated-access.no-access'),
					body: this.translocoService.translate('cloud.shared.elevated-access.no-access-description'),
					confirmLabel: this.translocoService.translate('cloud.shared.elevated-access.ok'),
					cancelLabel: ''
				}
			}
		);
		return dialogRef.afterClosed$;
	}
	/**
	 * Fetches the user's account info with the 'organizationDetails' projection
	 * to retrieve elevated request details. If an elevated request is present
	 * in the response, it is stored in organization store and the banner visibility is updated.
	 */
	public getElevatedRequestDetails(): void {
		this.apiUsersService
			.getSessionAccountInfo$Response({ projections: 'organizationDetails' })
			.pipe(
				switchMap((accountInfoResponse) => {
					const elevatedRequestResponse = accountInfoResponse.body.body?.elevatedRequest;
					if (elevatedRequestResponse) {
						this.elevatedAccessService.setElevatedRequest(<ElevatedRequestDto>elevatedRequestResponse);
						this.updateAccessRequestBanner();
					}
					return EMPTY;
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
	}

	/**
	 * Updates the visibility of the access request banner based on the
	 * current elevated request status. The banner is shown only when
	 * the request is in the 'Pending' state.
	 */
	public updateAccessRequestBanner(): void {
		const elevatedRequest = this.elevatedAccessService.getElevatedRequest();
		if (
			elevatedRequest?.requestStatus.toLowerCase() === ElevatedRequestStatus.Pending ||
			elevatedRequest?.requestStatus.toLowerCase() === ElevatedRequestStatus.Granted
		) {
			this.showAccessRequestBanner.set(true);
		} else {
			this.showAccessRequestBanner.set(false);
		}
	}

	/**
	 * Handles the approval flow for an elevated access request.
	 * Opens the Grant Access dialog, retrieves the selected duration,
	 * and triggers the elevated request update process.
	 */
	public approveElevatedRequest(): void {
		this.dialogService
			.openDialog(GrantAccessDialogComponent)
			.afterClosed$.pipe(
				switchMap((row) => {
					if (row?.data) {
						this.updateElevatedRequest(row?.data.durationMinutes);
					}
					return EMPTY;
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
	}

	/**
	 * Terminates an already granted elevated access request.
	 *
	 * - Updates the request status to `Terminate` via the API
	 * - Handles successful termination by showing a success message
	 * - Displays an error message in case the API call fails
	 *
	 * This method is triggered when the CSA user chooses to stop
	 * their granted elevated access before the expiry time.
	 */
	public stopGrantedAccess(): void {
		const elevatedRequest = this.elevatedAccessService.getElevatedRequest();
		this.elevatedAccessApiService
			.updateElevatedRequest(elevatedRequest?.elevatedRequestId || '', {
				requestStatus: ElevatedRequestStatus.Terminate
			})
			.pipe(
				map((updateResponse) => {
					this.handleElevatedAccessResponse(
						updateResponse.body.body.data,
						updateResponse.body.body.messageKey
					);
				}),
				catchError((error) => {
					const errorMessage = error.error;
					if (error.error.elevatedRequest) {
						this.elevatedAccessService.setElevatedRequest(error.error.elevatedRequest);
						this.updateAccessRequestBanner();
					}
					this.showSnackbarMessage(errorMessage.i18nKey || errorMessage.messageKey, errorMessage.message);
					return EMPTY;
				})
			)
			.subscribe();
	}

	/**
	 * Updates an elevated access request by granting access for the specified duration.
	 * Converts the duration (received as a string from the dialog) to an integer,
	 * sends the update request to the backend, updates the store with the response,
	 * refreshes the access request banner, and displays a success or error message.
	 *
	 * @param durationMinutes - Duration (in minutes) for which elevated access is granted.
	 */
	public updateElevatedRequest(durationMinutes: string, isExtend?: boolean): void {
		const elevatedRequest = this.elevatedAccessService.getElevatedRequest();
		const status = isExtend ? ElevatedRequestStatus.Extend : ElevatedRequestStatus.Granted;
		this.elevatedAccessApiService
			.updateElevatedRequest(elevatedRequest?.elevatedRequestId || '', {
				requestStatus: status,
				durationMinutes: parseInt(durationMinutes, 10) || 0
			})
			.pipe(
				map((updateResponse) => {
					this.handleElevatedAccessResponse(
						updateResponse.body.body.data,
						updateResponse.body.body.messageKey
					);
				}),
				catchError((error) => {
					const errorMessage = error.error;
					if (error.error.elevatedRequest) {
						this.elevatedAccessService.setElevatedRequest(error.error.elevatedRequest);
						this.updateAccessRequestBanner();
					}
					this.showSnackbarMessage(errorMessage.i18nKey || errorMessage.messageKey, errorMessage.message);
					return EMPTY;
				})
			)
			.subscribe();
	}

	/**
	 * Rejects the elevated access request.
	 * Sends an update request with status REJECTED, updates the store,
	 * refreshes the banner visibility, and shows a success or error message.
	 */
	public rejectElevatedRequest(): void {
		const elevatedRequest = this.elevatedAccessService.getElevatedRequest();
		this.elevatedAccessApiService
			.updateElevatedRequest(elevatedRequest?.elevatedRequestId || '', {
				requestStatus: ElevatedRequestStatus.Rejected
			})
			.pipe(
				map((updateResponse) => {
					this.handleElevatedAccessResponse(
						updateResponse.body.body.data,
						updateResponse.body.body.messageKey
					);
				}),
				catchError((error) => {
					const errorMessage = error.error;
					if (error.error.elevatedRequest) {
						this.elevatedAccessService.setElevatedRequest(error.error.elevatedRequest);
						this.updateAccessRequestBanner();
					}
					this.showSnackbarMessage(errorMessage.i18nKey || errorMessage.messageKey, errorMessage.message);
					return EMPTY;
				})
			)
			.subscribe();
	}

	/**
	 * Handles the response after an elevated access request is updated.
	 * Updates the elevated request state, refreshes the access request banner,
	 * and displays a snackbar message using the provided i18n key.
	 *
	 * @param updateResponse - The API response containing the updated ElevatedRequestDto.
	 * @param i18nKey - The translation key used to show the success or status message.
	 */
	public handleElevatedAccessResponse(updateResponse: ElevatedRequestDto, i18nKey: string): void {
		this.elevatedAccessService.setElevatedRequest(updateResponse);
		this.updateAccessRequestBanner();
		this.showSnackbarMessage(i18nKey);
	}

	/**
	 * Opens the Extend Access dialog when elevated access is already granted.
	 * Allows the user to add additional time to the existing access.
	 */
	public openExtendAccessDialog(): void {
		this.dialogService
			.openDialog(GrantAccessDialogComponent, {
				data: {
					isExtendAccess: true,
					expiryTime: {
						dateTime: this.formattedElevatedDetails()?.expiresAt
					}
				}
			})
			.afterClosed$.pipe(
				switchMap((row) => {
					if (row?.data) {
						this.updateElevatedRequest(row?.data.durationMinutes, true);
					}
					return EMPTY;
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe();
	}

	/**
	 * Cancels a pending elevated access request.
	 *
	 * - Sends a stop/cancel request to the API for the Granted request
	 * - Updates the elevated request state with the response data
	 * - Refreshes the access request banner visibility
	 * - Displays a success or error message via snackbar
	 *
	 * This method is triggered when a Elevated user wants to stop
	 * the Granted elevated access before the expiry time.
	 */
	public stopAccess(): void {
		const elevatedRequest = this.elevatedAccessService.getElevatedRequest();
		this.elevatedAccessApiService
			.stopAccess(elevatedRequest?.elevatedRequestId || '')
			.pipe(
				map((cancelResponse) => {
					this.handleElevatedAccessResponse(
						<ElevatedRequestDto>cancelResponse.body.body.data,
						cancelResponse.body.body?.messageKey ?? ''
					);
				}),
				catchError((error) => {
					const errorMessage = error.error;
					if (error.error.elevatedRequest) {
						this.elevatedAccessService.setElevatedRequest(error.error.elevatedRequest);
						this.updateAccessRequestBanner();
					}
					this.showSnackbarMessage(errorMessage.i18nKey || errorMessage.messageKey, errorMessage.message);
					return EMPTY;
				})
			)
			.subscribe();
	}

	/**
	 * Displays a translated or fallback message in the snackbar.
	 *
	 * @param i18nKey - Optional translation key used to fetch a localized message.
	 * @param message - Fallback message to be shown if translation key is not provided or translation is unavailable.
	 */
	public showSnackbarMessage(i18nKey?: string, message?: string): void {
		const translatedMessage = i18nKey ? this.elevatedAccessService.getTranslatedStrings(i18nKey) : message ?? '';
		if (translatedMessage) {
			this.snackBarService.open({
				message: translatedMessage,
				actionText: this.translocoService.translate('common.buttons.ok')
			});
		}
	}
}
